import { CONSENT_SAFETY_TIMEOUT_MS } from 'App/constants';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

const CONSENT_BANNER_SCRIPT_ID = 'cookieyes-banner';
const BANNER_SRC_PATTERN = /\/consentSDK\/banner[^/]*\.js/i;
/** Stale local banners reference rotated hashes under this CDN path. */
const CLIENT_DATA_URL_NEEDLE = 'cdn-cookieyes.com/client_data/';
const CONSENT_BANNER_LOAD_ERROR = 'ConsentBannerLoadError';
const SESSION_REPORT_KEY = 'consent_banner_outdated_reported';

/**
 * @returns {boolean}
 */
function hasReportedThisSession() {
    try {
        return sessionStorage.getItem(SESSION_REPORT_KEY) === '1';
    } catch {
        return false;
    }
}

function markReportedThisSession() {
    try {
        sessionStorage.setItem(SESSION_REPORT_KEY, '1');
    } catch {
        // ignore quota / private-mode failures; in-memory guard still applies
    }
}

/**
 * Only the self-hosted CookieYes banner script (`#cookieyes-banner` /
 * `/consentSDK/banner-*.js`). Outer `#cookieyes` failures are handled elsewhere.
 *
 * @param {EventTarget|null} target
 * @returns {boolean}
 */
function isOutdatedBannerScriptTarget(target) {
    if (!target || target.tagName !== 'SCRIPT') return false;
    if (target.id === CONSENT_BANNER_SCRIPT_ID) return true;

    const src = String(target.src || '');
    return BANNER_SRC_PATTERN.test(src);
}

function toUrlString(input) {
    if (typeof input === 'string') return input;
    if (input instanceof Request) return input.url;
    if (input && typeof input.url === 'string') return input.url;
    return '';
}

/**
 * Detects an outdated local CookieYes banner and reports once per browser session.
 *
 * Specific signals only (does not suppress or rewrite any other console errors):
 * 1. `#cookieyes-banner` / `/consentSDK/banner-*.js` fails to load
 * 2. Banner fetches to `cdn-cookieyes.com/client_data/*` fail (403/404 on rotated hashes)
 *
 * Performance:
 * - No-ops immediately if already reported this session
 * - Error listener early-returns unless the event target is the banner script
 * - Fetch wrap only inspects CookieYes client_data URLs, and is removed after
 *   the consent safety window (or right after the first report)
 *
 * @returns {() => void} Uninstall / cleanup function
 */
export function installConsentErrorHandler() {
    if (typeof window === 'undefined') return () => {};
    if (hasReportedThisSession()) return () => {};

    let reported = false;
    let fetchWrapped = false;
    const originalFetch = window.fetch.bind(window);

    const restoreFetch = () => {
        if (fetchWrapped && window.fetch === wrappedFetch) {
            window.fetch = originalFetch;
        }
        fetchWrapped = false;
    };

    const report = (error, tags) => {
        if (reported || hasReportedThisSession()) return;
        reported = true;
        markReportedThisSession();
        restoreFetch();

        try {
            captureCustomException(error, CONSENT_BANNER_LOAD_ERROR, {
                module: 'consent',
                ...tags,
            });
        } catch {
            console.warn('consent_banner_load_error');
        }
    };

    const onErrorCapture = (event) => {
        // Resource load failures only: target is the <script>; JS errors are ignored.
        // Never preventDefault / stopPropagation — other console errors stay untouched.
        if (!isOutdatedBannerScriptTarget(event?.target)) return;

        const src = String(event.target.src || '');
        report(new Error(`Consent banner script failed to load`), {
            reason: 'banner_script_load_failed',
            script_url: src || 'unknown',
        });
    };

    const wrappedFetch = async (input, init) => {
        const response = await originalFetch(input, init);

        // Cheap path: skip unless this looks like a CookieYes banner asset request.
        // Rotated hashes answer 403 or 404 depending on the CDN rule that matched.
        if (reported || response.ok) return response;

        const url = toUrlString(input);
        if (!url.includes(CLIENT_DATA_URL_NEEDLE)) return response;

        report(
            new Error(
                `Outdated consent banner asset (${response.status})`
            ),
            {
                reason: 'outdated_banner_asset',
                asset_url: url,
                status: String(response.status),
            }
        );

        return response;
    };

    window.addEventListener('error', onErrorCapture, { capture: true });
    window.fetch = wrappedFetch;
    fetchWrapped = true;

    // Banner assets load during init; drop the fetch wrap after that window.
    const fetchWrapTimeout = setTimeout(
        restoreFetch,
        CONSENT_SAFETY_TIMEOUT_MS
    );

    return () => {
        clearTimeout(fetchWrapTimeout);
        window.removeEventListener('error', onErrorCapture, { capture: true });
        restoreFetch();
    };
}
